import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router';
import { Mail, User } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { authService } from '../../services/authService';
import { MIN_PASSWORD_LENGTH } from '../../config/constants';
import AuthLayout from '../../components/auth/AuthLayout';
import AuthCard from '../../components/auth/AuthCard';
import SignupLegalNotice from '../../components/auth/SignupLegalNotice';
import PasswordField, { validatePassword } from '../../components/auth/PasswordField';
import ConfirmPasswordField, { validateConfirmPassword } from '../../components/auth/ConfirmPasswordField';
import Button from '../../components/ui/Button';
import logger from '../../utils/logger';

const getSignupErrorMessage = (err) => {
  if (err?.code === 'EMAIL_EXISTS') {
    return 'An account with this email already exists.';
  }

  return err?.message || 'An error occurred. Please try again.';
};

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const deletionOutcome = location.state?.accountDeletionOutcome;

  const [step, setStep] = useState(1);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [showPasswordHints, setShowPasswordHints] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [error, setError] = useState(() => {
    if (deletionOutcome?.status === 'pending') {
      return 'Deletion underway. We’ll email you when it’s complete.';
    }
    if (deletionOutcome?.status === 'completed') {
      return deletionOutcome.deletionMode === 'delete_household'
        ? 'Household deleted.'
        : 'Account deleted.';
    }
    return '';
  });
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const requestVersion = useRef(0);
  useLayoutEffect(() => {
    requestVersion.current += 1;
    setLoading(false);
    return () => { requestVersion.current += 1; };
  }, [email, fullName, location.key, step]);

  useEffect(() => {
    if (!['completed', 'pending'].includes(deletionOutcome?.status)) return;
    navigate('/signup', { replace: true, state: null });
  }, [deletionOutcome?.status, navigate]);

  const validateIdentityStep = () => {
    if (!fullName.trim()) {
      logger.errorEvent('signup_invalid_input', { field: 'fullName' });
      setError('Please enter your full name');
      return false;
    }

    if (!email.trim()) {
      logger.errorEvent('signup_invalid_input', { field: 'email' });
      setError('Please enter your email');
      return false;
    }

    return true;
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    const version = ++requestVersion.current;
    const isCurrentRequest = () => version === requestVersion.current;
    setError('');
    setErrorCode('');

    if (!validateIdentityStep()) {
      return;
    }

    setLoading(true);

    try {
      await authService.checkSignupEmail(email);
      if (!isCurrentRequest()) return;
      setStep(2);
    } catch (err) {
      if (!isCurrentRequest()) return;
      setError(getSignupErrorMessage(err));
      setErrorCode(err.code || '');
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const version = ++requestVersion.current;
    const isCurrentRequest = () => version === requestVersion.current;
    setError('');
    setErrorCode('');
    setConfirmPasswordTouched(true);

    const nextPasswordError = validatePassword(password, MIN_PASSWORD_LENGTH);
    const nextConfirmPasswordError = validateConfirmPassword(password, confirmPassword);
    setPasswordError(nextPasswordError);
    setConfirmPasswordError(nextConfirmPasswordError);

    if (!validateIdentityStep() || nextPasswordError || nextConfirmPasswordError) {
      if (nextPasswordError) {
        logger.errorEvent('signup_invalid_input', { field: 'password' });
        setShowPasswordHints(true);
      }
      if (nextConfirmPasswordError) {
        logger.errorEvent('signup_invalid_input', { field: 'confirmPassword', reason: 'mismatch' });
      }
      return;
    }

    setLoading(true);

    try {
      await authService.signup(fullName, email, password);
      if (!isCurrentRequest()) return;
      navigate('/check-email', {
        state: {
          email: email.trim().toLowerCase(),
          fromSignup: true
        }
      });
    } catch (err) {
      if (!isCurrentRequest()) return;
      setError(getSignupErrorMessage(err));
      setErrorCode(err.code || '');
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  };

  const formTransition = {
    initial: { opacity: 0, x: 28 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
    transition: { duration: 0.2, ease: 'easeOut' }
  };

  return (
    <AuthLayout>
      <AuthCard>
        <AnimatePresence mode="wait" initial={false}>
          {step === 1 ? (
            <motion.div key="identity-step" {...formTransition}>
              <div className="auth-heading">
                <h1 className="mt-0 text-[2.25rem] font-bold tracking-[-0.03em] text-[var(--color-text-primary)]">
                  Create Account
                </h1>
                <p className="mt-2 max-w-[18rem] text-base leading-7 text-[var(--color-text-secondary)]">Join Vylo today.</p>
              </div>

              {error && (
                <div className="auth-error" role="alert">
                  <p>{error}</p>
                  {errorCode === 'EMAIL_EXISTS' && (
                    <Link
                      to="/login"
                      className="mt-2 inline-block text-sm text-[var(--color-text-secondary)] underline-offset-4 hover:text-[var(--color-text-primary)] hover:underline"
                    >
                      Try signing in
                    </Link>
                  )}
                </div>
              )}

              <form onSubmit={handleContinue} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="fullName" className="text-sm font-medium text-[var(--color-text-secondary)]">
                    Full name
                  </label>
                  <div className="relative mt-2">
                    <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
                    <input
                      id="fullName"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="John Doe"
                      className="h-12 w-full rounded-xl border border-[var(--color-border-strong)] bg-[var(--color-surface-input)] px-10 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-indigo-500/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/25"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="text-sm font-medium text-[var(--color-text-secondary)]">
                    Email
                  </label>
                  <div className="relative mt-2">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="h-12 w-full rounded-xl border border-[var(--color-border-strong)] bg-[var(--color-surface-input)] px-10 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-indigo-500/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/25"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  loading={loading}
                  loadingLabel="Checking email…"
                  fullWidth
                  className="mt-6"
                >
                  Continue
                </Button>
                <SignupLegalNotice />
              </form>
            </motion.div>
          ) : (
            <motion.div key="password-step" {...formTransition}>
              <div className="auth-heading">
                <h1 className="mt-0 text-[2.25rem] font-bold tracking-[-0.03em] text-[var(--color-text-primary)]">
                  Set Password
                </h1>
              </div>

              {error && (
                <div className="auth-error" role="alert">
                  <p>{error}</p>
                  {errorCode === 'EMAIL_EXISTS' && (
                    <Link
                      to="/login"
                      className="mt-2 inline-block text-sm text-[var(--color-text-secondary)] underline-offset-4 hover:text-[var(--color-text-primary)] hover:underline"
                    >
                      Try signing in
                    </Link>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <PasswordField
                  value={password}
                  onChange={(nextValue) => {
                    setPassword(nextValue);
                    if (passwordError) setPasswordError('');
                  }}
                  onBlur={() => {
                    setPasswordError(validatePassword(password, MIN_PASSWORD_LENGTH));
                  }}
                  error={passwordError}
                  minLength={MIN_PASSWORD_LENGTH}
                  recommendedLength={12}
                  showHints={showPasswordHints}
                />

                <ConfirmPasswordField
                  value={confirmPassword}
                  password={password}
                  onChange={(nextValue) => {
                    setConfirmPassword(nextValue);
                    if (confirmPasswordError) setConfirmPasswordError('');
                  }}
                  onBlur={() => {
                    setConfirmPasswordTouched(true);
                    setConfirmPasswordError(validateConfirmPassword(password, confirmPassword));
                    if (passwordError) setShowPasswordHints(true);
                  }}
                  touched={confirmPasswordTouched}
                  error={confirmPasswordError}
                />

                <Button
                  type="submit"
                  loading={loading}
                  loadingLabel="Creating account…"
                  fullWidth
                  className="mt-6"
                >
                  Create account
                </Button>

                <SignupLegalNotice />
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="auth-footer-divider flex items-center">
          <div className="h-px flex-1 border-t border-[var(--color-border)]" />
          <span className="px-3 text-xs text-[var(--color-text-muted)]">OR</span>
          <div className="h-px flex-1 border-t border-[var(--color-border)]" />
        </div>

        <p className="auth-footer text-[var(--color-text-secondary)]">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-[var(--color-text-primary)] underline-offset-4 hover:text-[var(--color-accent)] hover:underline"
          >
            Sign in
          </Link>
        </p>
      </AuthCard>
    </AuthLayout>
  );
};

export default SignUp;
